import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { VibeAuditEnvVarScopeReadSchema } from "./read-vocabularies.schema";

const VibeAuditSecretValueShape = {
  /// The secret-ref whose value moved. UUID, not the name — the name +
  /// scope pair is descriptive, the id is the join key back to the ref.
  vibeAppSecretRefId: z.string().uuid(),
  /// Env var name the secret materializes as. Descriptive only — carried
  /// so the audit feed reads "GH_TOKEN value set" without a follow-up
  /// query. NEVER the value.
  secretName: z.string().min(1),
  /// Which fleet the ref targets (ALL / PROD / STAGING). Derived from the
  /// generated enum (no hardcoded literals).
  scope: z.enum(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES)
};

/**
 * Emitted when a user stages a secret VALUE for a registered secret-ref
 * (`SetSecretValue`). The value is KMS-encrypted under the tenant CMK and
 * parked as a VibePendingSecretWrite; this audit row is written INSIDE the
 * same delete-prior-then-insert `$transaction` as the pending-write row so
 * the two land atomically. `actorUserId` IS set (the user who set the
 * value); `vibeAppId` IS set. The payload names the ref + scope but — by
 * the pointer model's core promise — carries NEITHER the value NOR the
 * ciphertext.
 */
export const VibeAuditSecretValueStagedPayloadSchema = z.object({
  eventType: z.literal("SECRET_VALUE_STAGED"),
  ...VibeAuditSecretValueShape
});
export type VibeAuditSecretValueStagedPayload = z.infer<
  typeof VibeAuditSecretValueStagedPayloadSchema
>;

/**
 * Emitted when the tenant's agent confirms it wrote a staged secret value
 * into the cluster's Vault and the ack purged the VibePendingSecretWrite
 * row. Written by the agent-authed ack use case. `actorUserId` is null (the
 * agent is a system actor); `vibeAppId` IS set so the per-app timeline shows
 * "value delivered to Vault". Same shape as STAGED — the ref + scope, never
 * the value.
 */
export const VibeAuditSecretValueWrittenPayloadSchema = z.object({
  eventType: z.literal("SECRET_VALUE_WRITTEN"),
  ...VibeAuditSecretValueShape
});
export type VibeAuditSecretValueWrittenPayload = z.infer<
  typeof VibeAuditSecretValueWrittenPayloadSchema
>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditSecretValueScopeRead = { scope: VibeAuditEnvVarScopeReadSchema };

export const VibeAuditSecretValueStagedReadPayloadSchema =
  VibeAuditSecretValueStagedPayloadSchema.extend(VibeAuditSecretValueScopeRead);
export const VibeAuditSecretValueWrittenReadPayloadSchema =
  VibeAuditSecretValueWrittenPayloadSchema.extend(VibeAuditSecretValueScopeRead);
